import { createSignal, Match, Show, Switch } from "solid-js";
import { ADRESSE_AVIS, lienAvis, type Avis } from "../avis";
import { envoyerAvis, type ResultatEnvoi } from "../donnees/avis";

type Etat = { etat: "saisie" } | { etat: "envoi" } | ResultatEnvoi;

/**
 * Où un lecteur dit ce qui va et ce qui ne va pas. Le message part directement
 * depuis le site (service serveur/, par Brevo). Si ce service n'est pas
 * disponible, la page propose d'ouvrir la messagerie avec le message rédigé.
 */
export function VueAvis(props: {
  adresse?: string | null;
  /** Injecté par les tests ; sinon l'envoi réel. */
  envoyer?: (avis: Avis, piege: string) => Promise<ResultatEnvoi>;
}) {
  const adresse = () => (props.adresse === undefined ? ADRESSE_AVIS : props.adresse);
  const [partie, setPartie] = createSignal("");
  const [message, setMessage] = createSignal("");
  const [auteur, setAuteur] = createSignal("");
  const [piege, setPiege] = createSignal("");
  const [etat, setEtat] = createSignal<Etat>({ etat: "saisie" });
  const avis = (): Avis => ({ partie: partie(), message: message(), auteur: auteur() });
  const pret = () => message().trim().length > 0 && etat().etat !== "envoi";

  const envoyer = async (e: SubmitEvent) => {
    e.preventDefault();
    if (!pret()) return;
    setEtat({ etat: "envoi" });
    const envoi = props.envoyer ?? ((a, p) => envoyerAvis(a, __VERSION_APPLI__, p));
    setEtat(await envoi(avis(), piege()));
  };

  const recommencer = () => {
    setMessage("");
    setPartie("");
    setEtat({ etat: "saisie" });
  };

  return (
    <div class="vue-texte">
      <section class="carte carte-parametre">
        <h2>Votre avis sur ce prototype</h2>
        <p class="explication">
          Ce site est un prototype : vos retours décident de ce qui sera gardé, corrigé ou retiré
          dans la version finale. Ce qui vous a été utile compte autant que ce qui vous a gêné.
        </p>

        <Show
          when={etat().etat !== "envoye"}
          fallback={
            <div class="avis-envoye" role="status">
              <p>
                <strong>Merci, votre retour a été envoyé.</strong>
              </p>
              <button type="button" class="bouton" onClick={recommencer}>
                Écrire un autre retour
              </button>
            </div>
          }
        >
          <form class="formulaire-avis" onSubmit={(e) => void envoyer(e)}>
            <label class="champ">
              <span>
                Partie du site concernée <span class="discret">(facultatif)</span>
              </span>
              <input
                type="text"
                maxLength={200}
                placeholder="Réseau des projets, Cultures et filières, fiche d'un projet…"
                value={partie()}
                onInput={(e) => setPartie(e.currentTarget.value)}
              />
            </label>

            <label class="champ">
              <span>Votre message</span>
              <textarea
                rows={7}
                required
                maxLength={5000}
                placeholder="Ce que vous avez cherché à faire, ce qui s'est passé, ce que vous attendiez…"
                value={message()}
                onInput={(e) => setMessage(e.currentTarget.value)}
              />
            </label>

            <label class="champ">
              <span>
                Nom ou structure <span class="discret">(facultatif)</span>
              </span>
              <input
                type="text"
                maxLength={200}
                autocomplete="organization"
                value={auteur()}
                onInput={(e) => setAuteur(e.currentTarget.value)}
              />
            </label>

            {/* Champ piège : invisible pour un humain, rempli par les robots. */}
            <label class="piege" aria-hidden="true">
              Ne pas remplir
              <input
                type="text"
                name="site"
                tabIndex={-1}
                autocomplete="off"
                value={piege()}
                onInput={(e) => setPiege(e.currentTarget.value)}
              />
            </label>

            <div class="formulaire-pied">
              <button type="submit" class="bouton actif" disabled={!pret()}>
                {etat().etat === "envoi" ? "Envoi…" : "Envoyer"}
              </button>
              <Switch>
                <Match when={etat().etat === "trop"}>
                  <span class="discret" role="alert">
                    Trop d'envois rapprochés : réessayez dans quelques minutes.
                  </span>
                </Match>
                <Match when={etat().etat === "refuse" && etat()}>
                  {(e) => (
                    <span class="discret" role="alert">
                      {(e() as { raison: string }).raison}
                    </span>
                  )}
                </Match>
                <Match when={etat().etat === "indisponible"}>
                  <span class="discret" role="alert">
                    L'envoi direct est indisponible pour le moment.{" "}
                    <Show when={adresse()}>
                      {(a) => (
                        <a class="lien-projet" href={lienAvis(a(), avis(), __VERSION_APPLI__)}>
                          Ouvrir votre messagerie
                        </a>
                      )}
                    </Show>
                  </span>
                </Match>
              </Switch>
            </div>
          </form>
        </Show>
      </section>
    </div>
  );
}
